import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import LedgerEntryFormDialog from "@/components/dialogs/LedgerEntryFormDialog";
import EmployeeAttendanceAdminView from "@/components/schedule/EmployeeAttendanceAdminView";
import api, { formatApiErrorDetail, fileUrl, downloadUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Eye, Download, Plus, Pencil, Trash2 } from "lucide-react";

const EDIT_FIELDS = ["name", "designation", "date_of_joining", "sow", "reporting_manager", "phone", "address", "dob", "bank_account_number", "ifsc_code", "account_holder_name", "stipend_amount"];

export default function EmployeeDetailSheet({ employeeId, open, onOpenChange, onUpdated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [employee, setEmployee] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [remarkText, setRemarkText] = useState("");
  const [docForm, setDocForm] = useState({ title: "", doc_type: "signed_agreement", file: null });
  const [ledgerDialogOpen, setLedgerDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!employeeId) return;
    const [empRes, docsRes, ledgerRes] = await Promise.all([
      api.get(`/employees/${employeeId}`),
      api.get(`/employees/${employeeId}/documents`),
      api.get(`/employees/${employeeId}/ledger`),
    ]);
    setEmployee(empRes.data);
    setDocuments(docsRes.data);
    setLedger(ledgerRes.data);
  };

  useEffect(() => {
    if (open && employeeId) load();
  }, [open, employeeId]);

  const uploadProfileFile = async (fileType, file) => {
    const fd = new FormData();
    fd.append("file_type", fileType);
    fd.append("file", file);
    try {
      await api.post(`/employees/${employeeId}/profile-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${fileType === "resume" ? "Resume" : "ID document"} uploaded`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteProfileFile = async (fileType) => {
    try {
      await api.delete(`/employees/${employeeId}/profile-file/${fileType}`);
      toast.success(`${fileType === "resume" ? "Resume" : "ID document"} removed`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitDocument = async () => {
    if (!docForm.title || !docForm.file) {
      toast.error("Title and file are required");
      return;
    }
    const fd = new FormData();
    fd.append("doc_type", docForm.doc_type);
    fd.append("title", docForm.title);
    fd.append("file", docForm.file);
    try {
      await api.post(`/employees/${employeeId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setDocForm({ title: "", doc_type: "signed_agreement", file: null });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const replaceDocument = async (docId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.put(`/employees/${employeeId}/documents/${docId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document file replaced");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await api.delete(`/employees/${employeeId}/documents/${docId}`);
      toast.success("Document removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitRemark = async () => {
    if (!remarkText.trim()) return;
    try {
      await api.post(`/employees/${employeeId}/remarks`, { text: remarkText });
      setRemarkText("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const startEditing = () => {
    setEditForm(Object.fromEntries(EDIT_FIELDS.map((f) => [f, employee[f] || ""])));
    setIsEditing(true);
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      await api.put(`/employees/${employeeId}`, editForm);
      toast.success("Employee details updated");
      setIsEditing(false);
      load();
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteEmployee = async () => {
    try {
      await api.delete(`/employees/${employeeId}`);
      toast.success("Employee removed from the system");
      onOpenChange(false);
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!employee) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="employee-detail-sheet">
        <SheetHeader>
          <div className="flex items-center justify-between pr-8">
            <div>
              <SheetTitle className="font-heading" data-testid="employee-sheet-name">{employee.name}</SheetTitle>
              <p className="text-sm text-[#4B5563]">{employee.designation}</p>
            </div>
            {isAdmin && !isEditing && (
              <div className="flex items-center gap-2">
                <button data-testid="edit-employee-button" onClick={startEditing} className="h-8 w-8 flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#4B5563] hover:bg-[#F8F9FA] transition-colors duration-200">
                  <Pencil className="h-4 w-4" />
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid="delete-employee-button" className="h-8 w-8 flex items-center justify-center rounded-md border border-[#E2E8F0] text-red-600 hover:bg-red-50 transition-colors duration-200">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="delete-employee-confirm-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove {employee.name} from the system?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently deletes their login access, HR documents and ledger records.
                        Any tasks or opportunities assigned to them will be unassigned, not deleted. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-employee-cancel-button">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="delete-employee-confirm-button" onClick={deleteEmployee} className="bg-red-600 hover:bg-red-700">
                        Remove Employee
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
        </SheetHeader>
        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="overview" data-testid="employee-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="documents" data-testid="employee-tab-documents">Documents</TabsTrigger>
            {isAdmin && <TabsTrigger value="remarks" data-testid="employee-tab-remarks">Remarks</TabsTrigger>}
            <TabsTrigger value="ledger" data-testid="employee-tab-ledger">Ledger</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="employee-tab-attendance">Attendance</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            {isEditing ? (
              <div className="space-y-3 border border-[#E2E8F0] rounded-md p-4 bg-[#F8F9FA]">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Name</Label>
                    <Input data-testid="edit-employee-name-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Designation</Label>
                    <Input data-testid="edit-employee-designation-input" value={editForm.designation} onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Date of Joining</Label>
                    <Input data-testid="edit-employee-doj-input" type="date" value={editForm.date_of_joining} onChange={(e) => setEditForm({ ...editForm, date_of_joining: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Reporting Manager</Label>
                    <Input data-testid="edit-employee-manager-input" value={editForm.reporting_manager} onChange={(e) => setEditForm({ ...editForm, reporting_manager: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Phone</Label>
                    <Input data-testid="edit-employee-phone-input" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Address</Label>
                    <Input data-testid="edit-employee-address-input" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Date of Birth</Label>
                    <Input data-testid="edit-employee-dob-input" type="date" value={editForm.dob} onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Scope of Work</Label>
                  <Textarea data-testid="edit-employee-sow-input" value={editForm.sow} onChange={(e) => setEditForm({ ...editForm, sow: e.target.value })} rows={2} />
                </div>
                <div className="border-t border-[#E2E8F0] pt-3">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#4B5563] mb-2">Bank & Compensation Details</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Account Holder Name</Label>
                      <Input data-testid="edit-employee-account-holder-input" value={editForm.account_holder_name} onChange={(e) => setEditForm({ ...editForm, account_holder_name: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Bank Account Number</Label>
                      <Input data-testid="edit-employee-bank-account-input" value={editForm.bank_account_number} onChange={(e) => setEditForm({ ...editForm, bank_account_number: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">IFSC Code</Label>
                      <Input data-testid="edit-employee-ifsc-input" value={editForm.ifsc_code} onChange={(e) => setEditForm({ ...editForm, ifsc_code: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Stipend Amount (₹)</Label>
                      <Input data-testid="edit-employee-stipend-input" type="number" value={editForm.stipend_amount} onChange={(e) => setEditForm({ ...editForm, stipend_amount: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveEdits} disabled={saving} data-testid="edit-employee-save-button" className="bg-[#00823B] hover:bg-[#006A2F] text-white">
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setIsEditing(false)} data-testid="edit-employee-cancel-button">Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="Email" value={employee.email} />
                  <Field label="Date of Joining" value={employee.date_of_joining} />
                  <Field label="Reporting Manager" value={employee.reporting_manager} />
                  <Field label="Phone" value={employee.phone} />
                  <Field label="Address" value={employee.address} className="col-span-2" />
                  <Field label="Date of Birth" value={employee.dob} />
                  <Field label="Personal Gmail (notifications)" value={employee.personal_gmail} />
                  <Field label="Scope of Work" value={employee.sow} className="col-span-2" />
                </div>
                <div className="border-t border-[#E2E8F0] pt-4">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#4B5563] mb-2">Bank & Compensation Details</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Field label="Account Holder Name" value={employee.account_holder_name} />
                    <Field label="Bank Account Number" value={employee.bank_account_number} />
                    <Field label="IFSC Code" value={employee.ifsc_code} />
                    <Field label="Stipend Amount" value={employee.stipend_amount != null ? `\u20B9${Number(employee.stipend_amount).toLocaleString("en-IN")}` : null} />
                  </div>
                </div>
              </>
            )}
            <div className="border-t border-[#E2E8F0] pt-4 space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#4B5563]">Admin Vault</h4>
              <div className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md">
                <span className="text-sm">Resume</span>
                <div className="flex items-center gap-2">
                  {employee.resume_path && (
                    <>
                      <a data-testid="employee-resume-view-link" href={fileUrl(employee.resume_path)} target="_blank" rel="noreferrer" className="text-xs text-[#00823B] hover:underline flex items-center gap-1">
                        <FileText className="h-3.5 w-3.5" /> View
                      </a>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid="employee-resume-delete-button" className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove Resume?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteProfileFile("resume")} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                  <label className="text-xs text-[#4B5563] cursor-pointer hover:underline" data-testid="employee-resume-upload-label">
                    {employee.resume_path ? "Replace" : "Upload"}
                    <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadProfileFile("resume", e.target.files[0])} />
                  </label>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md">
                <span className="text-sm">ID Document</span>
                <div className="flex items-center gap-2">
                  {employee.id_document_path && (
                    <>
                      <a data-testid="employee-iddoc-view-link" href={fileUrl(employee.id_document_path)} target="_blank" rel="noreferrer" className="text-xs text-[#00823B] hover:underline flex items-center gap-1">
                        <FileText className="h-3.5 w-3.5" /> View
                      </a>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid="employee-iddoc-delete-button" className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove ID Document?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteProfileFile("id_document")} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                  <label className="text-xs text-[#4B5563] cursor-pointer hover:underline" data-testid="employee-iddoc-upload-label">
                    {employee.id_document_path ? "Replace" : "Upload"}
                    <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadProfileFile("id_document", e.target.files[0])} />
                  </label>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="space-y-4 mt-4">
            <div className="border border-[#E2E8F0] rounded-md p-3 space-y-2">
              <Input data-testid="doc-upload-title-input" placeholder="Document title" value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} />
              <div className="flex gap-2">
                <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
                  <SelectTrigger data-testid="doc-upload-type-select" className="flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {isAdmin && <SelectItem value="onboarding_letter">Onboarding Letter</SelectItem>}
                    {isAdmin && <SelectItem value="sop">SOP</SelectItem>}
                    <SelectItem value="signed_agreement">Signed Agreement</SelectItem>
                  </SelectContent>
                </Select>
                <Input data-testid="doc-upload-file-input" type="file" onChange={(e) => setDocForm({ ...docForm, file: e.target.files?.[0] || null })} className="flex-1" />
              </div>
              <Button size="sm" onClick={submitDocument} data-testid="doc-upload-submit-button" className="bg-[#00823B] hover:bg-[#006A2F] text-white">
                <Plus className="h-3.5 w-3.5 mr-1" /> Upload Document
              </Button>
            </div>
            <div className="space-y-2">
              {documents.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No documents yet.</p>}
              {documents.map((d) => (
                <div key={d.id} data-testid={`document-row-${d.id}`} className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md hover:bg-[#F8F9FA] transition-colors duration-200">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1C1C1E] truncate">{d.title}</p>
                    <p className="text-xs text-[#4B5563]">{d.doc_type.replace(/_/g, " ")} · by {d.uploaded_by}</p>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <a href={fileUrl(d.file_path)} target="_blank" rel="noreferrer" data-testid={`document-view-${d.id}`} title="View"><Eye className="h-4 w-4 text-[#4B5563]" /></a>
                    <a href={downloadUrl(d.file_path, d.title)} data-testid={`document-download-${d.id}`} title="Download" className="text-[#00823B]">
                      <Download className="h-4 w-4" />
                    </a>
                    {isAdmin && (
                      <>
                        <label className="cursor-pointer text-[#4B5563] hover:text-[#1C1C1E]" data-testid={`document-replace-${d.id}`} title="Replace file">
                          <Pencil className="h-4 w-4" />
                          <input type="file" className="hidden" onChange={(e) => e.target.files[0] && replaceDocument(d.id, e.target.files[0])} />
                        </label>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`document-delete-${d.id}`} className="text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{d.title}"?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteDocument(d.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {isAdmin && (
            <TabsContent value="remarks" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Textarea data-testid="remark-input" placeholder="Add a performance remark or internal note..." value={remarkText} onChange={(e) => setRemarkText(e.target.value)} rows={3} />
                <Button size="sm" onClick={submitRemark} data-testid="remark-submit-button" className="bg-[#00823B] hover:bg-[#006A2F] text-white">Add Remark</Button>
              </div>
              <div className="space-y-2">
                {(employee.admin_remarks || []).length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No remarks yet.</p>}
                {(employee.admin_remarks || []).slice().reverse().map((r, idx) => (
                  <div key={idx} data-testid={`remark-item-${idx}`} className="p-3 border border-[#E2E8F0] rounded-md bg-[#F8F9FA]">
                    <p className="text-sm text-[#1C1C1E]">{r.text}</p>
                    <p className="text-xs text-[#4B5563] mt-1">{r.created_by} · {new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
          )}

          <TabsContent value="ledger" className="space-y-4 mt-4">
            {isAdmin && (
              <Button size="sm" onClick={() => setLedgerDialogOpen(true)} data-testid="ledger-add-entry-button" className="bg-[#00823B] hover:bg-[#006A2F] text-white">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Entry
              </Button>
            )}
            <table className="w-full text-sm border border-[#E2E8F0] rounded-md overflow-hidden">
              <thead className="bg-[#F8F9FA] text-xs uppercase text-[#4B5563]">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Month</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Amount</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {ledger.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-4 text-center text-[#4B5563]">No ledger entries yet.</td></tr>
                )}
                {ledger.map((l) => (
                  <tr key={l.id} data-testid={`ledger-row-${l.id}`} className="border-t border-[#E2E8F0]">
                    <td className="px-3 py-2">{l.month}</td>
                    <td className="px-3 py-2 capitalize">{l.type}</td>
                    <td className="px-3 py-2">₹{Number(l.amount).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2"><StatusBadge value={l.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TabsContent>

          <TabsContent value="attendance" className="space-y-4 mt-4">
            <EmployeeAttendanceAdminView employeeId={employeeId} />
          </TabsContent>
        </Tabs>
      </SheetContent>
      <LedgerEntryFormDialog open={ledgerDialogOpen} onOpenChange={setLedgerDialogOpen} employeeId={employeeId} onCreated={load} />
    </Sheet>
  );
}

function Field({ label, value, className }) {
  return (
    <div className={className}>
      <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-[#1C1C1E] font-medium">{value || "—"}</p>
    </div>
  );
}
